<template>
	<div class="adminMeetingAssignementas">
		<div class="flex items-center mb-4">
			<div class="h-2 rounded-lg overflow-clip bg-gray-2 w-full">
				<div class="h-full rounded-lg bg-blue" :style="{ width: overallTaskPercent }"></div>
			</div>
			<div class="text-gray-1 text-sm ml-3">{{ overallTaskPercent }}</div>
		</div>

		<div class="flex flex-col gap-5">
			<div v-if="unassignedTasks.length > 0" class="mb-4">
				<div class="flex items-center mb-2">
					<div class="flex-initial w-2/5 text-gray-1 font-base font-bold truncate pr-4">Unassigned</div>
				</div>


				<m-meeting-task-item 
					v-for="(task) in unassignedTasks" 
					@delete="() => removeTask(task)"
					@openUserPop="() => emit('openUserPop', task)"
					:key="task?.task_id" 
					:meeting="meeting"
					:task="task" 
					:isAssigned="task.isAssigned" 
					:checked="task.is_completed"
					:getTaskUsers="getTaskUsers" 
					:viewAssignedUsers="viewAssignedUsers"
					:canEdit="canEdit"
					:class="{ 'checked': task.is_completed }" />

			</div>
			
			<div class="w-full" v-if="UserUtils.isAssignedToStakeOrWard(currentUser?.user_assign_wards || []) == 'stake'">
				<div  v-for="(ward, i) in taskByWard" :key="i">
					<div class="flex items-center mb-2">
						<div class="flex-initial w-2/5 text-gray-1 font-base font-bold truncate pr-3">{{ ward.ward }}</div>
						<div class="flex-1 overflow-clip bg-gray-2 rounded-lg h-1 my-auto">
							<div class="h-full rounded-lg bg-blue" :style="{ width: ward.percent }"></div>
						</div>
						<div class="flex-initial text-gray-1 text-sm ml-3 text-center">{{ward.percent}}</div>
					</div>
	
	
					<m-meeting-task-item 
						v-for="(task) in ward.tasks" 
						@toggleComplete="() => toggleCheck?.(task)"
						@delete="() => removeTask(task)"
						@openUserPop="() => emit('openUserPop', task)"
						:meeting="meeting"
						:key="task.task_id" 
						:task="task" 
						:canEdit="canEdit"
						:isAssigned="task.isAssigned" 
						:checked="task.is_completed"
						:getTaskUsers="getTaskUsers" 
						:viewAssignedUsers="viewAssignedUsers"
						:class="{ 'checked': task.is_completed }" />
						
						
				</div>
			</div>


			<div class="w-full" v-if="UserUtils.isAssignedToStakeOrWard(currentUser?.user_assign_wards || []) == 'ward'">
					<div  v-for="(position, i) in taskByPosition" :key="i">
						<div class="flex items-center mb-2">
							<div class="flex-initial w-2/5 text-gray-1 font-base font-bold truncate pr-3">{{ position.position }}</div>
							<div class="flex-1 overflow-clip bg-gray-2 rounded-lg h-1 my-auto">
								<div class="h-full rounded-lg bg-blue" :style="{ width: position.percent }"></div>
							</div>
							<div class="flex-initial text-gray-1 text-sm ml-3 text-center">{{position.percent}}</div>
						</div>


						<m-meeting-task-item 
							v-for="(task) in position.tasks" 
							@toggleComplete="() => toggleCheck?.(task)"
							@delete="() => removeTask(task)"
							@openUserPop="() => emit('openUserPop', task)"
							:meeting="meeting"
							:key="task.task_id" 
							:task="task" 
							:canEdit="canEdit"
							:isAssigned="task.isAssigned" 
							:checked="task.is_completed"
							:getTaskUsers="getTaskUsers" 
							:viewAssignedUsers="viewAssignedUsers"
							:class="{ 'checked': task.is_completed }" />
							
					</div>
			</div>


			<button @click="() => addNewAssignment()" class="flex items-center w-fit gap-3 mx-auto"><church-icon icon="plus-circle" /> Add task</button>
		</div>
	</div>
</template>

<script setup >

import GoalApi from '@/apis/goalApis';
import MeetingApis from '@/apis/meetingApis';
import { computed, defineProps, defineEmits, onBeforeMount, ref } from 'vue';
import UserUtils from '@/helpers/userUtils';
import store from '@/store';

/* ========== PROPS ===== */
const props = defineProps({
	byWard : {type : Array, default : () => []},
	individualTask: { type : Object, default: () => {} },
	getTaskUsers: {type : Function, default: () => {}},
	viewAssignedUsers: {type : Function, default: () => {}},
	toggleCheck: {type : Function, default: () => {}},
	meeting : {type : Object, default : () => {}},
	canEdit : {type : Boolean, default : () => false},
	wards : {type : Array, default : () => []},
})

/* ======== EMIT ======== */
const emit = defineEmits(['update:meeting', 'openUserPop'])

/* ========== STATES ========== */
const userList = ref([]);

/* ======= COMPUTED ======== */
/**
 * Logged in user
 */
const currentUser = computed(() => store.state.user || null)

/**
 * Oveall task completion percentage
 */
const overallTaskPercent = computed(() => {
	const totalCompleted = (props.individualTask.completed || 0) + (props.byWard?.reduce((acc, ward) => acc + (ward.completed || 0), 0) || 0); 
	const totalTask = (props.individualTask.total || 0) + (props.byWard?.reduce((acc, ward) => acc + (ward.total || 0), 0) || 0);
	return totalTask ? `${Math.round((totalCompleted / totalTask) * 100)}%` : '0%';
})


/**
 * Get the list of unassigned tasks
 */
const unassignedTasks = computed(() => {
	const tasks = props.meeting?.task_assignments || []
	return tasks.filter(t => !t.assign_to_user && t.assign_to == 'none')
})


/**
 * Group tasks by position if currentUser is assigned to ward
 */
const taskByPosition = computed(() => {
	let tasksAssignments = props.meeting?.task_assignments?.filter?.(ts => ts.assign_to_user) || []

	/* Add position to task users */
	tasksAssignments = tasksAssignments.map(ts => {
		const users = ts?.assign_to_user?.map(u => {
			const user = userList.value?.find(u2 => Number(u2.ID) == Number(u.ID))
			return {
				...u,
				assign_position : user?.assign_position || '',
			}
		})
		return {
			...ts,
			assign_to_user : users,
		}
	})

	const positions = userList.value?.filter(u => tasksAssignments?.filter(ts => ts.assign_to_user)?.find(ts => ts?.assign_to_user?.find?.(tu => Number(tu?.ID) == Number(u?.ID))))
	.filter(u => u?.assign_position)
	.filter((a,b,c) => c.findIndex(cf => cf.assign_position == a.assign_position)  === b) // remove duplicates

	console.log('positions', positions, tasksAssignments)

	return positions.map(p => {
		let tasks = tasksAssignments?.filter(ts => ts?.assign_to_user?.find(tu => tu.assign_position == p.assign_position))

		/**
		 * Remove users from task 
		 */
		tasks = tasks.map(ts => {
			const assign_to_user = ts?.assign_to_user?.filter(tu => tu.assign_position == p.assign_position)

			return {...ts, assign_to_user}
		})

		const completed = tasks?.filter(t => t.is_completed).length || 0
		return {
			position : p.assign_position,
			tasks : tasks,
			percent : `${Math.round((completed / tasksAssignments?.length) * 100)}%`
		}
	})
})


/**
 * Group task by ward
 */
const taskByWard = computed(() => {
	let tasksAssignments = props.meeting?.task_assignments?.filter?.(ts => ts.assign_to_user) || []

	/* Add assigned ward to task users */
	tasksAssignments = tasksAssignments.map(ts => {
		const users = ts?.assign_to_user?.map(u => {
			const user = userList.value?.find(u2 => Number(u2.ID) == Number(u.ID))
			return {
				...u,
				assign_ward : user?.assign_ward || [],
			}
		})
		return {
			...ts,
			assign_to_user : users,
		}
	})

	let list = props.wards?.map(w => {
		let tasks = tasksAssignments?.filter(ts => ts?.assign_to_user?.find(tu => tu.assign_ward?.includes(w.site_domain)))

		/**
		 * Remove users from task 
		 */
		tasks = tasks.map(ts => {
			const assign_to_user = ts?.assign_to_user?.filter(tu => tu.assign_ward?.includes(w.site_domain))

			return {...ts, assign_to_user}
		})

		const completed = tasks?.filter(t => t.is_completed).length || 0

		return {
			ward : w.header?.trim() || w.site_domain,
			tasks : tasks,
			percent : `${Math.round((completed / tasksAssignments?.length) * 100)}%`
		}
	})

	// remove list that has no tasks
	list = list.filter(l => l.tasks?.length > 0)


	return list;
})


/* ============ METHODS ========== */

/**
 * Add new assignment
 */
const addNewAssignment = () => {
	const params = {
		type : 'individual',
		assign_to_user : '',
		meeting_id : props.meeting?.id,
		task_name : '',
	}
	MeetingApis.addAssignment({params, callback : (data) => {
		console.log('new meeting', data)

		emit('update:meeting', {
			...props.meeting,
			task_assignments : data?.task_assignments?.value || [],
		})
	}})
}
 
/**
 * Remove task 
 * @param {Object} task
 */
const removeTask = (task) => {
	const params = {
		meeting_id : props.meeting?.id,
		task_id : task.task_id,
		type : 'individual'
	}
	const meeting  = props.meeting?.task_assignments?.filter?.(t => t.task_id != task.task_id) || []
	emit('update:meeting', {
		...props.meeting,
		task_assignments : meeting,
	})

	MeetingApis.removeTask({...params})
}


/* =========== ON LOAD ============= */
onBeforeMount(() => {
	// get the list of users
	GoalApi.getGoalUsers({callback : (data) => userList.value = data})


})
</script>
